<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    persistent
    @hide="onDialogHide"
  >
    <q-card
      class="q-dialog-plugin"
      style="min-width:60vw"
    >
      <q-card-section class="row">
        <div
          class="text-h6 text-ellipsis col"
        >
          {{ $t('sessions.modals.session.dialogTitleEdit') }}
        </div>
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>

      <div
        v-if="isAlohaIntegration"
        class="q-pa-md"
      >
        <banner
          type="warning"
          :message="$t('sessions.modals.session.unrenderedActivitiesWarning')"
        />
      </div>

      <q-form @submit="onSubmit">
        <q-card-section class="col-12 row q-col-gutter-md">
          <client-users-selector
            v-model="sessionUser"
            :client="sessionClient"
            :readonly="!hasSessionClient"
            class="col-12"
          />
          <date-input
            v-model="sessionDate"
            :label="$t('sessions.modals.sessionActivity.labelStartDate')"
            outlined
            :options="startTimestampLimit"
            :error-message="$t('common.dateValidator')"
            class="col-4"
          />
          <time-input
            v-model="editSession.runTimestamp"
            :label="$t('sessions.modals.sessionActivity.labelStartTime')"
            outlined
            preserve-date
            :error-message="$t('common.timeValidator')"
            class="col-4"
          />
          <q-input
            v-model="sessionDuration"
            :label="$t('sessions.modals.session.labelDuration')"
            outlined
            :rules="[val => stringToDuration(val) > 0
              || $t('sessions.modals.session.inputDurationValidator')]"
            lazy-rules
            :readonly="!editSession.isDurationOverridden"
            mask="#:##:##"
            fill-mask="0"
            input-class="text-right"
            class="col-4"
          >
            <template #append>
              <universal-tooltip
                show-tooltip
                :text="syncText"
              >
                <q-btn
                  flat
                  round
                  dense
                  :icon="syncIcon"
                  @click="onSwitchDurationSync"
                />
              </universal-tooltip>
            </template>
          </q-input>
        </q-card-section>
        <q-card-actions
          align="right"
        >
          <q-btn
            v-close-popup
            flat
            :label="$t('common.buttonCancel')"
            color="secondary"
          />
          <q-btn
            flat
            :label="$t('common.buttonSave')"
            :disable="!isFormValid"
            type="submit"
            color="primary"
            data-e2e="button-save"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>
<script>
import DateInput from 'components/DateInput';
import Banner from 'components/UI/Banner';
import ClientUsersSelector from 'components/UI/ClientUsersSelector';
import TimeInput from 'components/UI/TimeInput';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { Timestamp } from 'firebase/firestore';
import { get, pick } from 'lodash';
import { mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';

export default {
  name: 'EditSessionDetailsDialog',

  components: { ClientUsersSelector, DateInput, TimeInput, Banner, UniversalTooltip },
  mixins: [Toolbox],

  props: {
    session: {
      type: Object,
      default: null,
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const { formatDate, adjustDate, getMonth, getDate, getFullYear, addToDate } = useTime();
    return { formatDate, adjustDate, getMonth, getDate, getFullYear, addToDate };
  },

  data() {
    return {
      sessionDate: null,
      sessionDuration: null,
      editSession: {
        isDurationOverridden: false,
        runBy: null,
        runByName: null,
        runTimestamp: null,
        duration: null,
      },

      startTimestampLimit: (d) => {
        return d <= this.formatDate(Date.now(), 'yyyy/MM/dd');
      },
    };
  },

  computed: {
    ...mapState(auth, ['connectedClinic']),

    syncIcon() {
      return this.editSession.isDurationOverridden
        ? 'sync_disabled'
        : 'sync';
    },

    syncText() {
      return this.editSession.isDurationOverridden
        ? this.$t('sessions.modals.session.durationNotSynced')
        : this.$t('sessions.modals.session.durationSynced');
    },

    isFormValid() {
      return (!!this.editSession.runBy)
        && (this.editSession.runTimestamp instanceof Timestamp)
        && (this.sessionDate instanceof Timestamp);
    },

    hasSessionClient() {
      return this.sessionClient !== null;
    },

    sessionClient() {
      return get(this.editSession, 'client', null);
    },

    sessionUser: {
      get() {
        return {
          user: get(this.editSession, 'runBy', null),
          userName: get(this.editSession, 'runByName', null),
        };
      },
      set(value) {
        this.editSession.runBy = get(value, 'user', null);
        this.editSession.runByName = get(value, 'userName', null);
      },
    },

    isAlohaIntegration() {
      return this.connectedClinic.integration === 'aloha';
    },
  },

  watch: {
    session: {
      immediate: true,
      deep: true,
      handler(session) {
        this.editSession = pick(
          session,
          ['runTimestamp', 'duration', 'runBy', 'runByName', 'client', 'isDurationOverridden'],
        );
        this.sessionDate = get(this.editSession, 'runTimestamp', null);
        this.sessionDuration = this.formatDuration(this.editSession.duration || 0);
      },
    },
  },

  methods: {

    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    onCancelClick() {
      // we just need to hide dialog
      this.hide();
    },

    async onSubmit() {
      const adjustedDate = this.sessionDate.toDate();
      const day = this.getDate(adjustedDate);
      const month = this.getMonth(adjustedDate);
      const year = this.getFullYear(adjustedDate);
      this.editSession.runTimestamp = Timestamp.fromDate(
        this.adjustDate(this.editSession.runTimestamp, { year, month, day }),
      );
      this.editSession.duration = this.stringToDuration(this.sessionDuration);
      this.editSession.finishTimestamp = Timestamp.fromDate(
        this.addToDate(this.editSession.runTimestamp.toDate(), { seconds: this.editSession.duration }),
      );

      const sessionChanges = pick(
        this.editSession,
        ['runBy', 'runByName', 'runTimestamp', 'finishTimestamp', 'duration', 'isDurationOverridden'],
      );
      this.$emit(
        'ok',
        sessionChanges,
      );
      this.hide();
    }, // onSubmit()

    onSwitchDurationSync() {
      this.editSession.isDurationOverridden = !this.editSession.isDurationOverridden;
    },

  },

};
</script>
